import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import T from "../components/T";
import { Brain, Moon, Users, PawPrint, Wallet, Coffee, ChevronRight, Wind, HeartHandshake } from "lucide-react";

const ICONS = {
  brain: Brain, moon: Moon, users: Users, paw: PawPrint,
  wallet: Wallet, coffee: Coffee, wind: Wind, heart_hands: HeartHandshake,
};

// `lgbtq` is appended at the end by default but lifted to position 2 when
// the user has indicated a same-sex or queer relationship context in
// onboarding. The other cards' order is preserved exactly as today —
// existing users see no change.
const BASE_CARDS = [
  { slug: "breath",         route: "/sanctuary/breath",         icon: "wind",        key: "card_breath" },
  { slug: "nervous-system", route: "/sanctuary/nervous-system", icon: "brain",       key: "card_ns" },
  { slug: "community",      route: "/sanctuary/community",      icon: "users",       key: "card_community" },
  { slug: "cycle",          route: "/sanctuary/cycle",          icon: "moon",        key: "card_cycle" },
  { slug: "pets",           route: "/sanctuary/pets",           icon: "paw",         key: "card_pets" },
  { slug: "financial",      route: "/sanctuary/financial",      icon: "wallet",      key: "card_financial" },
  { slug: "comfort",        route: "/sanctuary/comfort",        icon: "coffee",      key: "card_comfort" },
];

const LGBTQ_CARD = {
  slug: "lgbtq",
  route: "/sanctuary/lgbtq",
  icon: "heart_hands",
  key: "card_lgbtq",
};

function orderedCards(partnerGender) {
  const personalised = partnerGender === "woman" || partnerGender === "nonbinary";
  if (personalised) {
    // After Breath, then the LGBTQ+ module so it's visible without scrolling.
    return [BASE_CARDS[0], LGBTQ_CARD, ...BASE_CARDS.slice(1)];
  }
  // Default placement — appended at the end so existing users' card order
  // remains unchanged but the resource is still discoverable.
  return [...BASE_CARDS, LGBTQ_CARD];
}

export default function Sanctuary() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const cards = orderedCards(profile?.partner_gender);

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-sanctuary-hub">
        <p className="font-script text-2xl text-brand">{t("v11.sanctuary_hub.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight"><T i18nKey="v11.sanctuary_hub.h" /></h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("v11.sanctuary_hub.sub")}</p>
      </div>

      <div className="px-8 pb-10 grid grid-cols-1 gap-3">
        {cards.map((c) => {
          const Icon = ICONS[c.icon];
          return (
            <button
              key={c.slug}
              data-testid={`sanctuary-card-${c.slug}`}
              onClick={() => navigate(c.route)}
              className="w-full text-left rounded-3xl bg-card border border-border p-5 hover:border-brand/50 transition-all flex items-start gap-4"
            >
              <span className="w-11 h-11 grid place-items-center rounded-full bg-brand/10 text-brand shrink-0">
                <Icon className="w-5 h-5" />
              </span>
              <div className="flex-1">
                <div className="font-serif text-xl">{t(`v11.sanctuary_hub.${c.key}_title`)}</div>
                <p className="text-sm text-foreground/65 mt-1 leading-relaxed">
                  {t(`v11.sanctuary_hub.${c.key}_desc`)}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-foreground/40 mt-3" />
            </button>
          );
        })}
      </div>
    </AppShell>
  );
}
